import { Skeleton, TableBody } from '@mui/material';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import React from 'react';
import { Order, getComparator } from 'utils/tableUtils';

export interface LiveSpaceUtilizationData {
  name: string;
  occupancy: number;
  occupants: number;
  capacity: number;
}

interface LiveSpaceUtilizationHeadCell {
  id: keyof LiveSpaceUtilizationData;
  label: string;
  numeric: boolean;
}

interface EnhancedLiveSpaceUtilizationTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof LiveSpaceUtilizationData
  ) => void;
  order: Order;
  orderBy: string;
  showOccupancy: boolean;
}

function getPeakHeadCells(showOccupancy: boolean): LiveSpaceUtilizationHeadCell[] {
  const cells: LiveSpaceUtilizationHeadCell[] = [
    {
      id: 'name',
      numeric: false,
      label: 'Space'
    }
  ];

  if (showOccupancy) {
    cells.push({
      id: 'occupancy',
      numeric: true,
      label: 'Occupancy'
    });
  }

  cells.push({
    id: 'occupants',
    numeric: true,
    label: 'Occupants'
  });

  cells.push({
    id: 'capacity',
    numeric: true,
    label: 'Capacity'
  });

  return cells;
}

function EnhancedLiveSpaceUtilizationTableHead(props: EnhancedLiveSpaceUtilizationTableProps) {
  const { showOccupancy, order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof LiveSpaceUtilizationData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const peakHeadCells = getPeakHeadCells(showOccupancy);

  return (
    <TableHead>
      <TableRow>
        {peakHeadCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: 'bold' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function LiveSpaceUtilizationTable(props: {
  liveSpaceRows: LiveSpaceUtilizationData[];
  loading: boolean;
  showOccupancy: boolean;
}) {
  const { liveSpaceRows, loading, showOccupancy } = props;

  const [liveSpaceOrder, setLiveSpaceOrder] = React.useState<Order>('desc');
  const [liveSpaceOrderBy, setLiveSpaceOrderBy] =
    React.useState<keyof LiveSpaceUtilizationData>('occupants');

  const sortedPeakRows = React.useMemo(
    () => liveSpaceRows.slice().sort(getComparator(liveSpaceOrder, liveSpaceOrderBy)),
    [liveSpaceOrder, liveSpaceOrderBy, liveSpaceRows]
  );

  const handleLiveSpaceColumnSort = (
    _: React.MouseEvent<unknown>,
    property: keyof LiveSpaceUtilizationData
  ) => {
    const isAsc = liveSpaceOrderBy === property && liveSpaceOrder === 'asc';
    setLiveSpaceOrder(isAsc ? 'desc' : 'asc');
    setLiveSpaceOrderBy(property);
  };

  if (loading) {
    return <Skeleton variant="rectangular" height={600} />;
  }

  return (
    <>
      <TableContainer>
        <Table size="small" aria-label="Live space utilization">
          <EnhancedLiveSpaceUtilizationTableHead
            order={liveSpaceOrder}
            orderBy={liveSpaceOrderBy}
            onRequestSort={handleLiveSpaceColumnSort}
            showOccupancy={showOccupancy}
          />
          <TableBody>
            {sortedPeakRows.map((row) => {
              return (
                <TableRow hover tabIndex={-1} key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  {showOccupancy && (
                    <TableCell align="right">{Math.round(row.occupancy * 100)}%</TableCell>
                  )}
                  <TableCell align="right">{row.occupants}</TableCell>
                  <TableCell align="right">{row.capacity}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
